import type { ComponentType, JSX, PropsWithChildren } from 'react';

import { TrackingContext } from '@change-corgi/core/react/tracking';

import type { ErrorStateDetails } from 'src/app/shared/utils/async';
import { isError, isLoaded } from 'src/app/shared/utils/async';

import type { StarterDashboardV2PageCombinedState, StarterDashboardV2PagePrefetchedContext } from './hook';
import { usePageContext } from './hook';
import { StarterDashboardV2PageDataContextProvider } from './pageData';
import { StarterDashboardV2PageFcmContextProvider } from './pageFcm';
import { StarterDashboardV2PageFcmExperimentsContextProvider } from './pageFcmExperiments';
import { StarterDashboardV2BannerStateProvider } from './pageState';

export type ErrorHandlerProps = { context: ErrorStateDetails<StarterDashboardV2PageCombinedState> };

type Props = {
	slug: string;
	prefetchedData: StarterDashboardV2PagePrefetchedContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export function StarterDashboardV2PageContextProvider({
	children,
	slug,
	prefetchedData,
	errorHandler: ErrorHandler,
	loadingFallback,
}: PropsWithChildren<Props>): JSX.Element | null {
	const pageContextState = usePageContext(slug, prefetchedData);

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData, pageFcm, pageFcmExperiments } = pageContextState;

	return (
		<StarterDashboardV2PageDataContextProvider pageData={pageData}>
			<StarterDashboardV2PageFcmContextProvider pageFcm={pageFcm}>
				<StarterDashboardV2PageFcmExperimentsContextProvider pageFcmExperiments={pageFcmExperiments}>
					<StarterDashboardV2BannerStateProvider>
						<TrackingContext properties={{ petition_id: pageData.data.petition.id }}>{children}</TrackingContext>
					</StarterDashboardV2BannerStateProvider>
				</StarterDashboardV2PageFcmExperimentsContextProvider>
			</StarterDashboardV2PageFcmContextProvider>
		</StarterDashboardV2PageDataContextProvider>
	);
}
